import request from 'axios';
import pick from 'lodash/pick';
import reduce from 'lodash/reduce';
import isEqual from 'lodash/isEqual';
import omit from 'lodash/omit';
import { BASE_64_REGEXP } from 'common/variables';
import { browserHistory } from 'react-router';
import { saveSpecializations } from 'profile/ducks/specializations';
import idx from 'idx';
import partial from 'lodash/partial';
import { setBannerError } from 'error';
import { getServerResponse } from 'common/server/ducks';

export const FETCH_PROFILE = 'crowdspring/profile/FETCH_PROFILE';
export const FETCH_PROFILE_REQUEST = 'crowdspring/profile/FETCH_PROFILE_REQUEST';
export const FETCH_PROFILE_FAILURE = 'crowdspring/profile/FETCH_PROFILE_FAILURE';
export const FETCH_PROFILE_SUCCESS = 'crowdspring/profile/FETCH_PROFILE_SUCCESS';

export const UPDATE_PROFILE = 'crowdspring/profile/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_REQUEST = 'crowdspring/profile/UPDATE_PROFILE_REQUEST';
export const UPDATE_PROFILE_FAILURE = 'crowdspring/profile/UPDATE_PROFILE_FAILURE';
export const UPDATE_PROFILE_SUCCESS = 'crowdspring/profile/UPDATE_PROFILE_SUCCESS';

export const COMPLETED_PROJECTS = 'completed';
export const OPEN_PROJECTS = 'open';

export function filterProfile(response) {
  const { projects_published } = response.data;

  const data = {
    ...response.data,
    publishedProjects: projects_published,
    activeProjects: projects_published.filter(({ status }) => (status === OPEN_PROJECTS)),
    completedProjects: projects_published.filter(({ status }) => (status === COMPLETED_PROJECTS)),
  };

  return {
    ...response,
    data
  };
}

export const redirectIfInactive = (res, user) => {
  const isActive = idx(user, _ => _.data.is_active);
  if (!isActive && browserHistory) {
    browserHistory.push('/inactive-user/');
  } else if (!isActive && res) {
    res.redirect(301, '/inactive-user/');
    res.end();
  }

  return user;
};

export const fetchProfileIfNeeded = profileUsername => (dispatch, getState) => {
  /* profile already fetched */
  const { view } = getState().profile;
  if (view.data && view.data.username === profileUsername) {
    return Promise.resolve({ data: view.data });
  }

  const opts = {
    method: 'get',
    url: `/api/v1/user-profiles/${profileUsername}/`,
  };
  const redirect = partial(redirectIfInactive, getServerResponse(getState()));

  const promise = request(opts).then(filterProfile).then(redirect);

  /* Handle missing profile.
     In case `browserHistory` is not available, it means server-rendering is being performed
     and the 404 error will be handled on the request level.
     Otherwise, user is redirected to /404. */
  promise.catch((response) => {
    if (response.status === 404 && browserHistory) {
      browserHistory.push('/404');
    }
  });

  dispatch({
    type: FETCH_PROFILE,
    promise,
  });

  return promise;
};

export function processImages(formData) {
  const { avatar, profile_bg } = formData;
  return {
    ...formData,
    avatar: avatar ? avatar.replace(BASE_64_REGEXP, '') : '',
    profile_bg: profile_bg ? profile_bg.replace(BASE_64_REGEXP, '') : '',
  };
}

export function getDifference(formData, initialValues) {
  const processed = processImages(formData);
  const keys = reduce(processed, (result, value, key) => (
    isEqual(value, initialValues[key]) ? result : result.concat(key)
  ), []);

  return pick(processed, keys);
}

export function saveProfile(formData) {
  const { id, specializations } = formData;
  const url = `/api/v1/users/${id}/`;
  return (dispatch, getState) => {
    dispatch({ type: UPDATE_PROFILE });

    const user = getState().user.profile_data;
    let updatedSpecializations = [];

    return saveSpecializations(user, specializations).then((response) => {
      updatedSpecializations = response ? response.data : updatedSpecializations;
      const initialData = getState().profile.view.data;
      const data = getDifference(omit(formData, 'specializations'), initialData);
      return request.patch(url, data);
    }).then((response) => {
      dispatch({
        type: UPDATE_PROFILE_SUCCESS,
        payload: {
          ...response.data,
          specializations: updatedSpecializations
        }
      });
      browserHistory.push(`/users/${user.username}`);
    }).catch((response) => {
      /* try to get error details in case of a 400 */
      let bannerTitle = '';
      let bannerContent = '';
      if (response.status === 400 && response.data && Object.keys(response.data)[0]) {
        const field = Object.keys(response.data)[0];
        bannerTitle = `Invalid ${field}`;
        bannerContent = response.data[field][0];
      }
      dispatch(setBannerError(bannerTitle, bannerContent));
      dispatch({
        type: UPDATE_PROFILE_FAILURE,
        payload: response
      });
    });
  };
}
